import { useState, useEffect } from 'react';
import { Guest, GuestRow } from './GuestRow';
import { InvitationModal } from './InvitationModal';
import { WaitingListModal } from './WaitingListModal';
import { ListCheck, Mail, Loader2 } from 'lucide-react';
import { Event } from '../../../services/EventService';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';

export const GuestList = ({ eventId, guestLimit }: { eventId: string; guestLimit: number }) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWaitingListOpen, setIsWaitingListOpen] = useState(false);
  const [guestList, setGuestList] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar usuarios del evento
  useEffect(() => {
    if (eventId) {
      fetchEventUsers();
    }
  }, [eventId]);

  const fetchEventUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await Event.getEventUsers(eventId);
      
      if (error) {
        setError('Error loading event users');
        console.error('Error fetching event users:', error);
        return;
      }

      setGuestList(data || []);
    } catch (error) {
      setError('Error loading event users');
      console.error('Error fetching event users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar invitados por estado para estadísticas
  const approvedGuests = guestList.filter(guest => 
    guest.approval_status === 'approved' && !guest.is_on_waiting_list
  );
  
  // Solo waiting list son los que están explícitamente en waiting list
  const waitingGuests = guestList.filter(guest => 
    guest.is_on_waiting_list === true
  );
  
  const handleSendInvitations = async (emails: string[]) => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const results = await Promise.all(
        emails.map(email => Event.inviteUser(eventId, { email }, user.id))
      );
      
      const successful = results.filter(r => !r.error).length;
      const failed = results.filter(r => r.error).length;
      
      if (successful > 0) {
        await fetchEventUsers();
      }
      
      if (failed > 0) {
        setError(`${successful} invitations sent, ${failed} failed`);
      } else {
        setError(null);
      }
    } catch (error) {
      setError('Error sending invitations');
      console.error('Error sending invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (guestId: string, newStatus: 'approved' | 'rejected') => {
    if (!user?.id) return;
    
    try {
      const { error } = await Event.updateEventUserApproval(guestId, newStatus, user.id);
      
      if (error) {
        setError('Error updating approval status');
        console.error('Error updating approval:', error);
        return;
      }
      
      setGuestList((prevGuests: any) => 
        prevGuests.map((guest: any) => 
          guest.id === guestId 
            ? { 
                ...guest, 
                approval_status: newStatus, 
                approved_at: newStatus === 'approved' ? new Date().toISOString() : null,
                rejected_at: newStatus === 'rejected' ? new Date().toISOString() : null
              }
            : guest
        )
      );
      
      // Si se aprueba a alguien, procesar waiting list por si hay espacio
      if (newStatus === 'approved') {
        await Event.processWaitingList(eventId);
        await fetchEventUsers();
      }
    } catch (error) {
      setError('Error updating approval status');
      console.error('Error updating approval:', error);
    }
  };

  const handleRemoveGuest = async (guestId: string) => {
    if (!user?.id) return;
    
    try {
      const { error } = await Event.removeEventUser(guestId, user.id);
      
      if (error) {
        setError('Error removing guest');
        console.error('Error removing guest:', error);
        return;
      }
      
      setGuestList(prevGuests => prevGuests.filter(guest => guest.id !== guestId));
      
      // Procesar waiting list para promover usuarios si hay espacio
      await Event.processWaitingList(eventId);
      await fetchEventUsers();
    } catch (error) {
      setError('Error removing guest');
      console.error('Error removing guest:', error);
    }
  };

  const handleMoveFromWaitingList = async (guestId: string) => {
    if (!user?.id) return;
    
    try {
      // Primero, remover de la waiting list
      const { error: removeError } = await Event.removeFromWaitingList(guestId);
      
      if (removeError) {
        setError('Error moving guest from waiting list');
        console.error('Error removing from waiting list:', removeError);
        return;
      }
      
      // Actualizar el estado local
      setGuestList((prevGuests: any) => 
        prevGuests.map((guest: any) => 
          guest.id === guestId 
            ? { 
                ...guest, 
                is_on_waiting_list: false,
                waiting_list_position: null,
                // Si el evento requiere aprobación, mantenerlo como pending
                // Si no requiere aprobación, aprobar automáticamente
                approval_status: 'pending' // O 'approved' dependiendo de tu lógica
              }
            : guest
        )
      );
      
      // Refrescar la lista para asegurar consistencia
      await fetchEventUsers();
      
    } catch (error) {
      setError('Error moving guest from waiting list');
      console.error('Error moving from waiting list:', error);
    }
  };

  if (loading) {
    return (
      <div className="border bg-white rounded-lg p-4 mt-4 w-full">
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border bg-white rounded-lg p-4 mt-4 w-full">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-end gap-3">
            <h2 className="text-xl font-bold">
              <span className="text-3xl font-normal">{approvedGuests.length}</span>/{guestLimit === 0 ? '∞' : guestLimit}
            </h2>
            <h2 className="text-lg">Guests</h2>
          </div>
          <div className="flex space-x-8 text-sm text-muted-foreground">
            <Button
              variant="ghost"
              className="flex items-center gap-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50 p-0 h-auto font-normal"
              onClick={() => setIsWaitingListOpen(true)}
            >
              <ListCheck size={16} />
              Waiting list {waitingGuests.length > 0 && `(${waitingGuests.length})`}
            </Button>
            <Button
              variant="ghost"
              className="flex items-center gap-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50 p-0 h-auto font-normal"
              onClick={() => setIsModalOpen(true)}
            >
              <Mail size={16} />
              Send invitations
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px] relative">
            <thead className="text-muted-foreground text-left border-b">
              <tr>
                <th className="pb-2 w-12">Guests</th>
                <th className="pb-2 w-64"></th>
                <th className="pb-2 w-64">Email</th>
                <th className="pb-2 w-64">Status</th>
                <th className="pb-2 w-32 sticky right-0 bg-white border-l border-gray-200 text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {guestList.map((guest) => (
                <GuestRow 
                  key={guest.id} 
                  guest={guest} 
                  onStatusChange={handleStatusChange}
                  onRemove={handleRemoveGuest}
                />
              ))}
            </tbody>
          </table>
        </div>

        {guestList.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No guests yet</p>
            <p className="text-sm">Send invitations to get started</p>
          </div>
        )}
      </div>

      <InvitationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSendInvitations={handleSendInvitations}
      />

      <WaitingListModal
        isOpen={isWaitingListOpen}
        onClose={() => setIsWaitingListOpen(false)}
        waitingGuests={waitingGuests}
        onStatusChange={handleStatusChange}
        onRemove={handleRemoveGuest}
        onMoveFromWaitingList={handleMoveFromWaitingList}
      />
    </>
  );
};
